const BACKOFF_STEPS_MS = [
  1 * 60 * 1000,
  5 * 60 * 1000,
  30 * 60 * 1000,
  4 * 60 * 60 * 1000,
]

/**
 * In-memory per-id backoff for storage transfers. PowerSync's AttachmentQueue
 * retries failed transfers on every sync pass, which spams the network and
 * console when an object is permanently missing or refused. Callers check
 * `isBackingOff` before touching the network and record each real failure:
 * the window grows 1m → 5m → 30m → 4h (capped).
 *
 * State is in-memory only; on reload the queue makes one attempt per item
 * before backoff re-establishes.
 */
export function createTransferBackoff() {
  const entries = new Map<string, { failures: number; nextAttemptAt: number }>()
  return {
    isBackingOff(id: string) {
      const entry = entries.get(id)
      return Boolean(entry && Date.now() < entry.nextAttemptAt)
    },
    /** Returns the failure count and the delay until the next attempt. */
    recordFailure(id: string) {
      const failures = (entries.get(id)?.failures ?? 0) + 1
      const delayMs =
        BACKOFF_STEPS_MS[Math.min(failures - 1, BACKOFF_STEPS_MS.length - 1)]
      entries.set(id, { failures, nextAttemptAt: Date.now() + delayMs })
      return { failures, delayMs }
    },
    clear(id: string) {
      entries.delete(id)
    },
  }
}

/** Thrown in place of a transfer while its id is backing off. */
export class TransferBackoffSkip extends Error {
  constructor(public readonly attachmentId: string) {
    super(`Transfer skipped — backing off for ${attachmentId}`)
  }
}
